import { Button, ButtonVariants, Organizations, useAuth } from "@app/shared";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pages } from "../../..";
import { useFetch } from "../../../../../hooks";
import { Icons } from "../../../../shared";

interface OrganizationStepProps {
    onNext: (selectedOrgId: string, organizationName: string) => void;
}

export const OrganizationStep: React.FC<OrganizationStepProps> = ({ onNext }) => {
    const { currentOrganizationId, user } = useAuth();
    const navigate = useNavigate();
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(currentOrganizationId);
    const [allOrgs, , fetchHelpers] = useFetch(Organizations.getAllOptions, []);

    const selectedOrg = allOrgs?.find((org) => org.value === selectedOrgId);
    const hasMultipleOrgs = (allOrgs?.length ?? 0) > 1;

    useEffect(() => {
        if (currentOrganizationId && !selectedOrgId) {
            setSelectedOrgId(currentOrganizationId);
        }
    }, [currentOrganizationId, selectedOrgId]);

    const handleSelectOrganization = (orgId: string) => {
        setSelectedOrgId(orgId);
    };

    const handleContinue = () => {
        if (selectedOrgId && selectedOrg) {
            onNext(selectedOrgId, selectedOrg.label || "");
        }
    };

    const handleAddNewOrganization = () => {
        navigate(Pages.AddOrganization.getUrl());
    };

    if (fetchHelpers.isLoading) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <p className="text-neutral-600">Loading organizations...</p>
                </div>
            </div>
        );
    }

    if (!allOrgs || allOrgs?.length === 0) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="text-center py-12">
                    <div className="w-16 h-16 bg-warmGray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icons.BusinessOutline className="w-8 h-8 text-warmGray-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">No Organizations Found</h2>
                    <p className="text-neutral-600 mb-6">You don't seem to have access to any organizations. Please contact your administrator.</p>
                    <Button onClick={handleAddNewOrganization} variant={ButtonVariants.Primary}>
                        Create New Organization
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icons.BusinessOutline className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Select Organization</h2>
                <p className="text-neutral-600 mb-4">{hasMultipleOrgs ? "Choose the organization where you want to set up your device" : "Confirm your organization for device setup"}</p>
                <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-neutral-700 font-medium">{user?.name}</span>
                </div>
            </div>

            <div className="mb-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">{hasMultipleOrgs ? "Available Organizations" : "Your Organization"}</h3>
                <div className="grid gap-3">
                    {allOrgs.map((org) => (
                        <div key={org.value} className={`group p-5 border rounded-xl transition-all duration-200 ${selectedOrgId === org.value ? "border-brand-primary bg-terracotta-50 shadow-md ring-1 ring-brand-primary ring-opacity-30 shadow-brand-glow" : "border-warmGray-200 hover:border-warmGray-300 hover:bg-warmGray-50"}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center cursor-pointer" onClick={() => handleSelectOrganization(org.value!)}>
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 transition-colors ${selectedOrgId === org.value ? "bg-brand-primary" : "bg-warmGray-100 group-hover:bg-warmGray-200"}`}>
                                        <Icons.BusinessOutline className={`w-6 h-6 ${selectedOrgId === org.value ? "text-white" : "text-warmGray-600"}`} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-neutral-900 flex items-center mb-1">{org.label}</h4>
                                        {currentOrganizationId === org.value && (
                                            <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></div>
                                                Current
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    {selectedOrgId === org.value && (
                                        <div className="w-5 h-5 bg-brand-primary rounded-full flex items-center justify-center mr-3">
                                            <div className="w-2 h-2 bg-white rounded-full"></div>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleContinue}
                                        disabled={selectedOrgId !== org.value}
                                        className={`p-2 rounded-lg transition-all ${
                                            selectedOrgId === org.value
                                                ? "bg-brand-primary hover:bg-brand-primary-darkened cursor-pointer"
                                                : "cursor-default"
                                        }`}
                                    >
                                        <Icons.ChevronRight className={`w-5 h-5 transition-colors ${
                                            selectedOrgId === org.value ? "text-white" : "text-warmGray-400"
                                        }`} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>


            {hasMultipleOrgs && (
                <>
                    <div className="flex items-center mb-6">
                        <div className="flex-1 border-t border-warmGray-200"></div>
                        <span className="px-4 text-warmGray-500 text-sm font-medium">OR</span>
                        <div className="flex-1 border-t border-warmGray-200"></div>
                    </div>

                    <button onClick={handleAddNewOrganization} className="flex w-full p-6 border-2 border-dashed border-warmGray-300 rounded-xl transition-all hover:border-brand-primary hover:bg-terracotta-50 group">
                        <div className="flex items-center justify-center mx-auto">
                            <div className="w-10 h-10 bg-warmGray-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-brand-primary transition-colors">
                                <Icons.AddOutline className="w-5 h-5 text-warmGray-600 group-hover:text-white transition-colors" />
                            </div>
                            <span className="text-neutral-700 font-semibold">Create New Organization</span>
                        </div>
                    </button>
                </>
            )}

            <div className="bg-gradient-to-r from-warmGray-50 to-white border border-warmGray-100 rounded-xl p-6 mt-6">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.BusinessOutline className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-2">About Organizations</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">Organizations help you manage devices and locations separately for different companies, departments, or clients. Each organization has its own set of location groups, facilities, and devices.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
